/**
 * Maps backend property (Mongo/multilingual) to client display shape.
 * @param {Object} p - Backend property (_id, title: { en, ar }, address: { en, ar }, etc.)
 * @param {string} lang - 'en' or 'ar'
 * @returns {Object} - { id, title, address, location, price, image, type, category, bedrooms, bathrooms, code, featured, ... }
 */
/** Pick string from multilingual field (string or { en, ar }) for lang */
const pickText = (value, lang) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return value[lang] ?? value.en ?? value.ar ?? '';
};

export const mapBackendProperty = (p, lang = 'en') => {
  if (!p) return null;
  const id = p._id ?? p.id;
  const title = pickText(p.title, lang);
  const address = pickText(p.address, lang);
  const city = pickText(p.city, lang);
  const location = city || address;
  const description = pickText(p.description, lang);
  const image = (p.images && p.images[0]) || p.livingRoomPhoto || (p.roomPhotos?.[0]?.photoUrl) || '';
  const propertyType = (p.propertyType || p.type || '').toLowerCase();
  const typeDisplay = propertyType ? propertyType.charAt(0).toUpperCase() + propertyType.slice(1) : '';
  return {
    ...p,
    id,
    _id: id,
    title,
    address,
    location,
    description,
    price: p.price ?? 0,
    image,
    images: p.images || [],
    video: (p.videos && p.videos[0]) || '',
    videos: p.videos || [],
    type: typeDisplay || (typeof p.type === 'string' ? p.type : ''),
    propertyType: p.propertyType,
    category: p.isInCompound === true ? 'compound' : (p.category || 'separate'),
    bedrooms: p.rooms ?? p.bedrooms ?? 0,
    bathrooms: p.bathrooms ?? 0,
    rooms: p.rooms,
    sqft: p.size,
    size: p.size,
    code: p.shortId || p.code,
    featured: !!p.featured,
    packages: Array.isArray(p.packages) ? p.packages : [],
    maxGuests: p.maxGuests != null ? p.maxGuests : undefined,
    extraFeesPerPerson: p.extraFeesPerPerson != null ? p.extraFeesPerPerson : undefined,
  };
};

export const mapBackendProperties = (list, lang = 'en') => {
  if (!Array.isArray(list)) return [];
  return list.map(p => mapBackendProperty(p, lang)).filter(Boolean);
};
